/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  TableColumnProps,
  Tag,
  Typography
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { get, post } from '../requestNew';
import './index.scss';
import type { PenaltyLogItem } from './types';

const Option = Select.Option;
const TextArea = Input.TextArea;

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 }
};

const PENALTY_TYPE_MAP: Record<number, { label: string; color: string }> = {
  1: { label: '黄牌', color: 'orangered' },
  2: { label: '红牌', color: 'red' }
};

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 30, 50];

/** 下发方式：null=全部不传参；1=定时任务(机器)；2=人工下发 */
const PENALTY_SOURCE_ALL = '__ALL__';

const PENALTY_SOURCE_MAP: Record<number, { label: string; color: string }> = {
  1: { label: '机器', color: 'cyan' },
  2: { label: '人工', color: 'green' }
};

/** 处罚选手弹窗：处罚原因多选项（value 与展示一致，提交时用 ";" 拼接） */
const ISSUE_PENALTY_REASON_OPTIONS = [
  '有资金进出未上报',
  '报单不规范',
  '报单不规范，无净资产',
  '报单不规范，无总资产',
  '报单不规范，无持仓数据',
  '资金少于1万，不符合参赛资格',
  '选手主动要求退赛',
  '当日未报单',
  '连续获得3张黄牌',
  '累计获得6张黄牌',
  '连续获得5张黄牌',
  '累计获得15张黄牌',
  '选手未配合验证账户',
  '笔记不符合社区规定'
];

function PenaltyLog() {
  /** 工作台跳转带过来的筛选参数，每次进入页面时读取当前地址 */
  const initialQuery = useMemo(() => new URLSearchParams(window.location.search), []);
  const [gameSession, setGameSession] = useState<string>('');
  const [sessionList, setSessionList] = useState<Array<{ label: string; value: string }>>([]);
  const [penaltyType, setPenaltyType] = useState<number | undefined>(undefined);
  /** null 表示全部，不传 penaltySource；默认 2 人工 */
  const [penaltySource, setPenaltySource] = useState<number | null>(() => {
    const source = initialQuery.get('penaltySource');
    if (source === 'all') return null;
    if (source === '1' || source === '2') return Number(source);
    return 2;
  });
  /** 下发日期 YYYY-MM-DD，空则不传 penaltyDate；默认当天 */
  const [penaltyDateFilter, setPenaltyDateFilter] = useState(() => initialQuery.get('penaltyDate') || dayjs().format('YYYY-MM-DD'));
  const [gameNameFilter, setGameNameFilter] = useState('');
  const [openIdFilter, setOpenIdFilter] = useState('');

  const [dataList, setDataList] = useState<PenaltyLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // 处罚选手弹框
  const [visibleIssue, setVisibleIssue] = useState(false);
  const [issueUserOptions, setIssueUserOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [issueFetching, setIssueFetching] = useState(false);
  const [issueOpenId, setIssueOpenId] = useState('');
  const [issueGameSession, setIssueGameSession] = useState('');
  const [issuePenaltyDate, setIssuePenaltyDate] = useState<string>('');
  /** 处罚原因多选，提交时 join(";") 写入接口 penaltyReason */
  const [issuePenaltyReason, setIssuePenaltyReason] = useState<string[]>([]);
  /** 提交时未选处罚原因，用于展示错误态与辅助提示 */
  const [issuePenaltyReasonError, setIssuePenaltyReasonError] = useState(false);
  /** 处罚备注，对应接口 remark */
  const [issueRemark, setIssueRemark] = useState('');
  const issueFetchIdRef = useRef(0);

  // 编辑处罚弹框
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [editRecord, setEditRecord] = useState<PenaltyLogItem | null>(null);
  const [editReason, setEditReason] = useState('');
  const [editRemark, setEditRemark] = useState('');

  // 赛季列表（与笔记页一致）
  const getGameSessionList = () => {
    get('/gameConfig/getGameSessionList')
      .then((res: any) => {
        const { data } = res || {};
        if (data && Array.isArray(data) && data.length > 0) {
          const options = data.map((item: any) => ({ label: item, value: item }));
          const sorted = options.sort((a: any, b: any) => {
            const aVal = String(a.value);
            const bVal = String(b.value);
            const aNum = /^\d+$/.test(aVal);
            const bNum = /^\d+$/.test(bVal);
            if (aNum && bNum) return parseInt(bVal, 10) - parseInt(aVal, 10);
            if (aNum && !bNum) return -1;
            if (!aNum && bNum) return 1;
            return aVal.localeCompare(bVal);
          });
          setSessionList(sorted);
          setGameSession(sorted[0].value);
          setIssueGameSession(sorted[0].value);
          getList({ gameSession: sorted[0].value, pageNum: 1 });
        } else {
          getList();
        }
      })
      .catch(() => {
        Message.error('获取赛季列表失败');
        getList();
      });
  };

  const getList = (opts: Record<string, any> = {}) => {
    setLoading(true);
    const params: any = {
      gameSession: opts.gameSession !== undefined ? opts.gameSession : gameSession,
      openId: openIdFilter.trim() || undefined,
      penaltyType: opts.penaltyType !== undefined ? opts.penaltyType : penaltyType,
      isValid: 1,
      pageNum: opts.pageNum !== undefined ? opts.pageNum : pageNum,
      pageSize: opts.pageSize !== undefined ? opts.pageSize : pageSize
    };

    const source =
      opts.penaltySource !== undefined ? opts.penaltySource : penaltySource;
    if (source === 1 || source === 2) {
      params.penaltySource = source;
    }

    const dateStr =
      opts.penaltyDate !== undefined ? opts.penaltyDate : penaltyDateFilter;
    if (typeof dateStr === 'string' && dateStr.trim()) {
      params.penaltyDate = dateStr.trim();
    }

    // 只有当gameNameFilter不为空时，才添加gameName参数
    if (gameNameFilter.trim()) {
      params.gameName = gameNameFilter.trim();
    }

    get('/sys/umsUserPenaltyLog/list', params)
      .then((res: any) => {
        if (res?.code === 200) {
          const data = res?.data ?? {};
          const list = data.list ?? data.records ?? [];
          const totalCount = data.total ?? 0;
          setDataList(list);
          setTotal(totalCount);
        } else {
          Message.error(res?.message || '查询失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getGameSessionList();
  }, []);

  useEffect(() => {
    if (sessionList.length === 0) return;
    getList();
  }, [pageNum, pageSize]);

  const onFilterQuery = () => {
    setPageNum(1);
    getList({ pageNum: 1 });
  };

  const handleGameSessionChange = (val: string) => {
    setGameSession(val || '');
    setPageNum(1);
    getList({ gameSession: val || '', pageNum: 1 });
  };

  const handlePenaltyTypeChange = (val: number | undefined) => {
    setPenaltyType(val);
    setPageNum(1);
    getList({ penaltyType: val, pageNum: 1 });
  };

  const handlePenaltySourceChange = (val: string | number) => {
    const next: number | null = val === PENALTY_SOURCE_ALL ? null : Number(val);
    setPenaltySource(next);
    setPageNum(1);
    getList({ penaltySource: next, pageNum: 1 });
  };

  const debouncedFetchUser = useCallback(
    debounce((gameName: string) => {
      issueFetchIdRef.current = Date.now();
      const fetchId = issueFetchIdRef.current;
      setIssueFetching(true);
      setIssueUserOptions([]);
      get('/backend/umsUser/list', { gameName, pageNum: 1, pageSize: 20 }).then((res: any) => {
        if (issueFetchIdRef.current !== fetchId) return;
        setIssueFetching(false);
        if (res?.code === 200) {
          const list = res?.data?.list || [];
          setIssueUserOptions(
            list.map((item: any) => ({ label: item.gameName ?? item.openId, value: item.openId }))
          );
        }
      });
    }, 500),
    []
  );

  const openIssueModal = () => {
    setVisibleIssue(true);
    setIssueOpenId('');
    setIssuePenaltyDate(dayjs().format('YYYY-MM-DD'));
    setIssuePenaltyReason([]);
    setIssuePenaltyReasonError(false);
    setIssueRemark('');
    setIssueUserOptions([]);
    setIssueGameSession(sessionList.length > 0 ? sessionList[0].value : gameSession || '');
  };

  const closeIssueModal = () => {
    setVisibleIssue(false);
    setIssueOpenId('');
    setIssuePenaltyReason([]);
    setIssuePenaltyReasonError(false);
    setIssueRemark('');
  };

  const issueCard = (type: 1 | 2) => {
    if (!issueOpenId) {
      Message.error('请选择用户');
      return;
    }
    const session = issueGameSession || sessionList[0]?.value;
    if (!session) {
      Message.error('请选择处罚赛季');
      return;
    }
    if (!issuePenaltyDate) {
      Message.error('请选择处罚日期');
      return;
    }
    if (!issuePenaltyReason.length) {
      setIssuePenaltyReasonError(true);
      Message.error('请选择处罚原因，至少选择一项');
      return;
    }
    const url = type === 1 ? '/sys/umsUserPenaltyLog/issueYellowCard' : '/sys/umsUserPenaltyLog/issueRedCard';
    post(url, {
      openId: issueOpenId,
      gameSession: session,
      penaltyDate: issuePenaltyDate,
      penaltyReason: issuePenaltyReason.join(';'),
      remark: issueRemark.trim()
    }).then((res: any) => {
      if (res?.code === 200) {
        Message.success(type === 1 ? '下发黄牌成功' : '下发红牌成功');
        closeIssueModal();
        getList();
      } else {
        Message.error(res?.message || '操作失败');
      }
    }).catch(() => Message.error('网络错误'));
  };

  const openEditModal = (record: PenaltyLogItem) => {
    setEditRecord(record);
    setEditReason(record.penaltyReason ?? '');
    setEditRemark(record.remark ?? '');
    setVisibleEdit(true);
  };

  const closeEditModal = () => {
    setVisibleEdit(false);
    setEditRecord(null);
    setEditReason('');
    setEditRemark('');
  };

  const submitEdit = () => {
    if (!editRecord?.id) return;
    post('/sys/umsUserPenaltyLog/updatePenaltyReasonAndRemark', {
      id: editRecord.id,
      penaltyReason: editReason,
      remark: editRemark
    }).then((res: any) => {
      if (res?.code === 200) {
        Message.success('更新成功');
        closeEditModal();
        getList();
      } else {
        Message.error(res?.message || '更新失败');
      }
    }).catch(() => Message.error('网络错误'));
  };

  const cancelPenalty = (record: PenaltyLogItem) => {
    post('/sys/umsUserPenaltyLog/cancelPenalty', { id: record.id }).then((res: any) => {
      if (res?.code === 200) {
        Message.success('取消处罚成功');
        getList();
      } else {
        Message.error(res?.message || '操作失败');
      }
    }).catch(() => Message.error('网络错误'));
  };

  const columns: TableColumnProps<PenaltyLogItem>[] = [
    { title: '序号', width: 70, render: (_, __, i) => (pageNum - 1) * pageSize + i + 1 },
    { title: '赛季', dataIndex: 'gameSession', width: 80 },
    { title: 'openId', dataIndex: 'openId', width: 120 },
    { title: '参赛名', dataIndex: 'gameName', width: 120 },
    {
      title: '处罚类型',
      dataIndex: 'penaltyType',
      width: 100,
      render: (t: number) => {
        const c = PENALTY_TYPE_MAP[t];
        return c ? <Tag color={c.color}>{c.label}</Tag> : '-';
      }
    },
    {
      title: '下发方式',
      dataIndex: 'penaltySource',
      width: 90,
      render: (t: number | undefined) => {
        const c = t === 1 || t === 2 ? PENALTY_SOURCE_MAP[t] : undefined;
        return c ? <Tag color={c.color}>{c.label}</Tag> : '-';
      }
    },
    { title: '处罚日期', dataIndex: 'penaltyDate', width: 120, render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD') : '-') },
    { title: '处罚时间', dataIndex: 'createTime', width: 170, render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '-') },
    { title: '处罚原因', dataIndex: 'penaltyReason', width: 180, ellipsis: true },
    { title: '备注', dataIndex: 'remark', width: 100, ellipsis: true },
    {
      title: '操作',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="text" onClick={() => openEditModal(record)}>编辑</Button>
          <Popconfirm title="确定取消该条处罚？" onOk={() => cancelPenalty(record)}>
            <Button type="text" status="success">取消处罚</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="penalty-log table-flex">
      <Space style={{ flexWrap: 'wrap' }}>
        <div className="filter-item">
          <Typography.Text>赛季:</Typography.Text>
          <Select
            value={gameSession}
            onChange={handleGameSessionChange}
            style={{ width: 120 }}
            placeholder="请选择赛季"
            allowClear
          >
            <Option value="">全部</Option>
            {sessionList.map((item) => (
              <Option key={item.value} value={item.value}>{item.label}</Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>类型:</Typography.Text>
          <Select
            value={penaltyType}
            onChange={handlePenaltyTypeChange}
            style={{ width: 100 }}
            placeholder="请选择类型"
            allowClear
          >
            <Option value={1}>黄牌</Option>
            <Option value={2}>红牌</Option>
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>下发方式:</Typography.Text>
          <Select
            value={penaltySource === null ? PENALTY_SOURCE_ALL : penaltySource}
            onChange={handlePenaltySourceChange}
            style={{ width: 100 }}
            placeholder="下发方式"
          >
            <Option value={PENALTY_SOURCE_ALL}>全部</Option>
            <Option value={1}>机器</Option>
            <Option value={2}>人工</Option>
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>下发日期:</Typography.Text>
          <DatePicker
            value={penaltyDateFilter ? dayjs(penaltyDateFilter) : undefined}
            onChange={(val: any) =>
              setPenaltyDateFilter(val ? dayjs(val).format('YYYY-MM-DD') : '')
            }
            style={{ width: 140 }}
            placeholder="YYYY-MM-DD"
            allowClear
          />
        </div>
        <div className="filter-item">
          <Typography.Text>参赛名:</Typography.Text>
          <Input
            value={gameNameFilter}
            onChange={setGameNameFilter}
            style={{ width: 140 }}
            placeholder="请输入..."
            allowClear
          />
        </div>
        <div className="filter-item">
          <Typography.Text>openid:</Typography.Text>
          <Input
            value={openIdFilter}
            onChange={setOpenIdFilter}
            style={{ width: 140 }}
            placeholder="请输入..."
            allowClear
          />
        </div>
        <Button type="primary" className="filter-item" onClick={onFilterQuery}>
          查询
        </Button>
        <Button type="primary" className="filter-item" onClick={openIssueModal}>
          处罚选手
        </Button>
      </Space>

      <div className="table-content">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          data={dataList}
          className="table-scroll"
          scroll={{ y: '100%', x: '100%' }}
          pagination={{
            current: pageNum,
            pageSize,
            total,
            showTotal: true,
            sizeCanChange: true,
            pageSizeChangeResetCurrent: true,
            sizeOptions: PAGE_SIZE_OPTIONS,
            onChange: (num, size) => {
              setPageNum(num);
              if (size !== pageSize) setPageSize(size);
            }
          }}
        />
      </div>

      {/* 处罚选手弹框 */}
      <Modal
        title="处罚选手"
        visible={visibleIssue}
        onCancel={closeIssueModal}
        footer={null}
        unmountOnExit
        style={{ width: 500 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>用户名称:</Typography.Text>
          <Select
            showSearch
            options={issueUserOptions}
            placeholder="请输入..."
            filterOption={false}
            notFoundContent={
              issueFetching ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                  <Spin />
                </div>
              ) : null
            }
            onSearch={debouncedFetchUser}
            value={issueOpenId}
            onChange={setIssueOpenId}
            style={{ width: 280 }}
            allowClear
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚赛季:</Typography.Text>
          <Select
            value={issueGameSession}
            onChange={setIssueGameSession}
            style={{ width: 280 }}
            placeholder="请选择"
          >
            {sessionList.map((item) => (
              <Option key={item.value} value={item.value}>{item.label}</Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚日期:</Typography.Text>
          <DatePicker
            value={issuePenaltyDate ? dayjs(issuePenaltyDate) : undefined}
            onChange={(val) => setIssuePenaltyDate(val ? dayjs(val).format('YYYY-MM-DD') : '')}
            style={{ width: 280 }}
            placeholder="请选择日期"
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80, verticalAlign: 'top', paddingTop: 6 }}>
            <span style={{ color: 'rgb(var(--red-6))', marginRight: 2 }}>*</span>
            处罚原因:
          </Typography.Text>
          <span style={{ display: 'inline-block', verticalAlign: 'top' }}>
            <Select
              mode="multiple"
              value={issuePenaltyReason}
              onChange={(v: string[]) => {
                setIssuePenaltyReason(v);
                if (v.length) setIssuePenaltyReasonError(false);
              }}
              placeholder="必填，请至少选择一项"
              style={{ width: 280 }}
              allowClear
              status={issuePenaltyReasonError ? 'error' : undefined}
            >
              {ISSUE_PENALTY_REASON_OPTIONS.map(text => (
                <Option key={text} value={text}>
                  {text}
                </Option>
              ))}
            </Select>
            {issuePenaltyReasonError ? (
              <Typography.Text type="error" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                请选择处罚原因，至少选择一项
              </Typography.Text>
            ) : null}
          </span>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚备注:</Typography.Text>
          <Input
            value={issueRemark}
            onChange={setIssueRemark}
            placeholder="请输入..."
            style={{ width: 280 }}
          />
        </div>
        <Space style={{ marginTop: 24 }}>
          <Button type="primary" status="danger" onClick={() => issueCard(2)}>下发红牌</Button>
          <Button type="primary" style={{ background: '#ffb117', borderColor: '#ffb117' }} onClick={() => issueCard(1)}>下发黄牌</Button>
          <Button onClick={closeIssueModal}>取消</Button>
        </Space>
      </Modal>

      {/* 编辑处罚记录弹框 */}
      <Modal
        title="比赛管理 / 处罚编辑"
        visible={visibleEdit}
        onCancel={closeEditModal}
        onOk={submitEdit}
        unmountOnExit
        okText="确定"
        style={{ width: 500 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>用户名称:</Typography.Text>
          <Input value={editRecord?.gameName ?? editRecord?.nickname ?? '-'} disabled style={{ width: 280 }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚原因:</Typography.Text>
          <Input
            value={editReason}
            onChange={setEditReason}
            placeholder="请输入..."
            style={{ width: 280 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Text style={{ display: 'inline-block', width: 80 }}>处罚备注:</Typography.Text>
          <Input
            value={editRemark}
            onChange={setEditRemark}
            placeholder="请输入..."
            style={{ width: 280 }}
          />
        </div>
      </Modal>
    </div>
  );
}

export default PenaltyLog;
